import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import FilterHeaderCell, { ActiveFilters } from "../common/FilterHeaderCell";
import { filterOptions, matchesColumnFilters } from "../common/columnFilters";
import { Chip } from "@mui/material";
import Header from "../Header/Header";
import ConfirmDialog from "../common/ConfirmDialog";
import DisabledHint from "../common/DisabledHint";
import reduxStore from "../redux";
import { styles } from "../MainPage/MainPage";
import { messages } from "../common/notification";
import { useStoreListRefresh } from "../common/useStoreList";
import { setStoreMaster } from "../redux/action/storeMaster";
import { downloadCsv, mapWithConcurrency } from "../Reports/functions";
import { MultiSelect, cellSelectStyles, selectStyles } from "../Reports/selects";
import { pageOf, regionOptions, storesInRegions } from "../StoreAnalytics/functions";
import {
  assignStoreLocation,
  assignStoreRegion,
  fetchCities,
  fetchRegions,
  fetchStates,
} from "./api";
import {
  applyStorePlan,
  buildCsvAssignments,
  clearStoreSaved,
  csvStateNames,
  exportRows,
  fillCityIds,
  findByName,
  locationRows,
  matchesStoreSearch,
  parseCsv,
  rowStateNames,
  samplePlace,
  sampleRows,
  setCityEdit,
  setRegionEdit,
  setStateEdit,
  storeCity,
  storeCityId,
  storeMasterRows,
  storeSaveRequests,
  storeState,
  templateRows,
  STORE_SETUP_FIELDS,
} from "./functions";
// Laid out like Device Master, with its classes.
import "../Reports/Reports.css";
import "../StoreAnalytics/StoreAnalytics.css";
import { changedFirst, unsetCount, unsetFirst } from "../DeviceMaster/functions";
import "../DeviceMaster/DeviceMaster.css";
import "./StoreMaster.css";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250].map((size) => ({ value: size, label: `${size} rows` }));
const COLUMNS = ["Code", "Store Name", "Region", "State", "City"];

// The header filter for each of COLUMNS, in order: typed for the code and
// name, picked from a list for the location columns.
const COLUMN_FILTERS = [
  { field: "code", kind: "text" },
  { field: "name", kind: "text" },
  { field: "region", kind: "select" },
  { field: "state", kind: "select" },
  { field: "city", kind: "select" },
];
const FILTER_LABELS = Object.fromEntries(
  COLUMN_FILTERS.map(({ field }, index) => [field, COLUMNS[index]])
);

// How many region or city calls a save runs at once.
const SAVE_CONCURRENCY = 5;
// Shown on hover over an action that waits for the store list to load.
const LOADING_TITLE = "Loading stores, please wait...";

const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

const asOption = (value) => (value ? { value, label: value } : null);

// A city box still waiting for its city, after a new state was picked.
const needsCityStyles = (width) => {
  const base = cellSelectStyles(width);
  return {
    ...base,
    control: (style, state) => ({ ...base.control(style, state), borderColor: "#F5A524" }),
    placeholder: (style) => ({ ...style, color: "#B7791F" }),
  };
};

// The Store Master page (formerly Store Regions): each store's region, state
// and city, edited in the table and saved together. Admin only (AdminOnly
// wraps the route).
function StoreRegions() {
  const dispatch = useDispatch();
  const housesList = useSelector((state) => state.housesList);
  const { edits = {} } = useSelector((state) => state.storeMaster ?? {});
  const { refreshing, refresh } = useStoreListRefresh();

  const [regions, setRegions] = useState([]);
  const [states, setStates] = useState([]);
  // stateId -> its cities, read once each per visit when first needed.
  const [citiesByState, setCitiesByState] = useState({});
  const [loadingCities, setLoadingCities] = useState({});

  const [regionFilter, setRegionFilter] = useState([]);
  const [search, setSearch] = useState("");
  // field -> typed text or picked value, for the header filters.
  const [columnFilters, setColumnFilters] = useState({});
  const setColumnFilter = (field, value) =>
    setColumnFilters((current) => ({ ...current, [field]: value }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [saving, setSaving] = useState(false);
  const [downloadingLocations, setDownloadingLocations] = useState(false);

  // A read CSV waiting to be applied.
  const [csvName, setCsvName] = useState("");
  const [csvPlan, setCsvPlan] = useState(null);
  const [readingCsv, setReadingCsv] = useState(false);
  const fileInput = useRef(null);

  const loadRegions = useCallback(async () => {
    const result = await fetchRegions();
    if (result.success) setRegions(result.data);
    else messages.error(result.message);
  }, []);

  const loadStates = useCallback(async () => {
    const result = await fetchStates();
    if (result.success) setStates(result.data);
    else messages.error(result.message);
    return result.success ? result.data : [];
  }, []);

  useEffect(() => {
    loadRegions();
    loadStates();
    refresh();
  }, [loadRegions, loadStates, refresh]);

  const loadCities = useCallback(
    async (stateId) => {
      if (!stateId || citiesByState[stateId] || loadingCities[stateId]) return;
      setLoadingCities((current) => ({ ...current, [stateId]: true }));
      const result = await fetchCities(stateId);
      setLoadingCities(({ [stateId]: done, ...rest }) => rest);
      if (result.success) setCitiesByState((current) => ({ ...current, [stateId]: result.data }));
      else messages.error(result.message);
    },
    [citiesByState, loadingCities]
  );

  const stores = useMemo(
    () =>
      [...(housesList ?? [])]
        .filter((store) => store.code)
        .sort((a, b) => (a.houseName || "").localeCompare(b.houseName || "")),
    [housesList]
  );

  const regionFilterOptions = useMemo(() => regionOptions(stores, regions), [stores, regions]);
  const regionSelectOptions = useMemo(() => regions.map((name) => asOption(name)), [regions]);
  const stateSelectOptions = useMemo(
    () => states.map((state) => ({ value: state._id, label: state.name, state })),
    [states]
  );

  // Every store in the picked regions, before the search and header filters;
  // the select filters offer what these hold.
  const regionRows = useMemo(
    () =>
      storeMasterRows(
        storesInRegions(stores, regionFilter.map((option) => option.value)),
        edits
      ),
    [stores, regionFilter, edits]
  );
  const rows = useMemo(
    () =>
      regionRows.filter(
        (row) =>
          matchesStoreSearch(row, search) && matchesColumnFilters(row, columnFilters, COLUMN_FILTERS)
      ),
    [regionRows, search, columnFilters]
  );
  const columnFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        COLUMN_FILTERS.filter(({ kind }) => kind === "select").map(({ field }) => [
          field,
          filterOptions(regionRows, field),
        ])
      ),
    [regionRows]
  );

  const requests = useMemo(() => storeSaveRequests(edits), [edits]);
  const saveCount = useMemo(
    () =>
      new Set([
        ...Object.values(requests.regionGroups).flat(),
        ...Object.values(requests.locationGroups).flatMap((group) => group.storeCodes),
      ]).size,
    [requests]
  );
  // Edits for stores filtered out right now still count; they are kept until
  // saved or discarded.
  const editCount = Object.keys(edits).length;

  useEffect(() => setPage(1), [search, regionFilter, columnFilters]);

  // The state of a row, as an entry from /v1/location/states.
  const rowState = (row) =>
    row.stateId ? { _id: row.stateId, name: row.state } : findByName(states, row.state);

  const update = (change) => {
    const current = reduxStore.getState().storeMaster?.edits ?? {};
    dispatch(setStoreMaster({ edits: change(current) }));
  };

  // Region changes go to /v1/stores/region, one call per region, and location
  // changes to /v1/stores/location, one per city - each call carries every
  // store getting that value, and the calls run together. Each call that
  // succeeds clears its own edits; a failed one keeps them, so Save can be
  // pressed again.
  const save = async () => {
    const { regionGroups, locationGroups, incomplete } = storeSaveRequests(
      reduxStore.getState().storeMaster?.edits ?? {}
    );
    if (Object.keys(regionGroups).length === 0 && Object.keys(locationGroups).length === 0) {
      if (incomplete.length) messages.error("Pick a city for each new state before saving");
      return;
    }

    setSaving(true);
    const regionEntries = Object.entries(regionGroups);
    const locationList = Object.values(locationGroups);
    const [regionResults, locationResults] = await Promise.all([
      mapWithConcurrency(regionEntries, SAVE_CONCURRENCY, ([region, codes]) =>
        assignStoreRegion(codes, region)
      ),
      mapWithConcurrency(locationList, SAVE_CONCURRENCY, (group) =>
        assignStoreLocation(group.storeCodes, group.cityId)
      ),
    ]);

    let remaining = reduxStore.getState().storeMaster?.edits ?? {};
    let regionsSaved = 0;
    let locationsSaved = 0;
    regionEntries.forEach(([region, codes], i) => {
      if (regionResults[i].success) {
        remaining = clearStoreSaved(remaining, "region", codes);
        regionsSaved += codes.length;
      } else {
        messages.error(`${region}: ${regionResults[i].message}`);
      }
    });
    locationList.forEach((group, i) => {
      if (locationResults[i].success) {
        remaining = clearStoreSaved(remaining, "location", group.storeCodes);
        locationsSaved += group.storeCodes.length;
      } else {
        messages.error(`${group.city}, ${group.state}: ${locationResults[i].message}`);
      }
    });

    dispatch(setStoreMaster({ edits: remaining }));
    setSaving(false);

    const parts = [
      regionsSaved && `region for ${plural(regionsSaved, "store")}`,
      locationsSaved && `location for ${plural(locationsSaved, "store")}`,
    ].filter(Boolean);
    if (parts.length) messages.success(`Saved ${parts.join(" and ")}`);
    if (incomplete.length) {
      messages.error(`${plural(incomplete.length, "store")} still need a city before the location can be saved`);
    }
    if (parts.length) refresh();
  };

  // Asks first (ConfirmDialog), since discarded edits cannot be got back.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const discard = () => {
    setConfirmingDiscard(false);
    dispatch(setStoreMaster({ edits: {} }));
  };

  // Cities are listed per state, so the cities of every state the file names
  // are loaded before the plan is built. Nothing changes until Apply.
  const readCsv = async (file) => {
    setReadingCsv(true);
    try {
      const parsed = parseCsv(await file.text());

      let stateList = states;
      const fileCities = {};
      const stateNames = csvStateNames(parsed);
      if (stateNames.length) {
        if (stateList.length === 0) stateList = await loadStates();
        const named = stateNames.map((name) => findByName(stateList, name)).filter(Boolean);
        for (const namedState of named) {
          const result = await fetchCities(namedState._id);
          if (result.success) fileCities[namedState._id] = result.data;
        }
        setCitiesByState((current) => ({ ...current, ...fileCities }));
      }

      const locations = {};
      for (const store of stores) {
        locations[String(store.code).toUpperCase()] = {
          state: storeState(store),
          city: storeCity(store),
          cityId: storeCityId(store),
        };
      }

      const plan = buildCsvAssignments(parsed, {
        regions,
        storeCodes: stores.map((store) => store.code),
        states: stateList,
        citiesByState: fileCities,
        locations,
      });
      setCsvName(file.name);
      setCsvPlan(plan);
    } catch (error) {
      console.error("CSV read failed", error);
      messages.error("Could not read the file");
    } finally {
      setReadingCsv(false);
    }
  };

  const onFile = (event) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (file) readCsv(file);
  };

  // The file's changes become table edits, to review and then Save.
  const applyCsv = () => {
    if (!csvPlan || csvPlan.total === 0) return;
    update((current) => applyStorePlan(current, csvPlan));
    messages.success(
      `${plural(csvPlan.total, "store")} updated from ${csvName}. Review the highlighted rows, then Save changes.`
    );
    setCsvPlan(null);
    setCsvName("");
  };

  // The stores the CSV downloads cover: those shown (after the filters).
  const csvStores = rows.map((row) => row.store);

  // Fills in each row's cityId, which an upload needs to change a location,
  // from the cities of the states the rows name (read a few at a time).
  const withCityIds = async (rows) => {
    const stateList = states.length ? states : await loadStates();
    const named = rowStateNames(rows.filter((row) => !row.cityId))
      .map((name) => findByName(stateList, name))
      .filter(Boolean);
    const results = await mapWithConcurrency(named, 5, (state) => fetchCities(state._id));
    const fetched = {};
    named.forEach((state, i) => {
      if (results[i].success) fetched[state._id] = results[i].data;
    });
    setCitiesByState((current) => ({ ...current, ...fetched }));
    return fillCityIds(rows, stateList, { ...citiesByState, ...fetched });
  };

  const [preparingCsv, setPreparingCsv] = useState(false);
  const downloadWithCityIds = async (rows, fileName) => {
    setPreparingCsv(true);
    try {
      downloadCsv(await withCityIds(rows), fileName);
    } catch (error) {
      console.error("CSV download failed", error);
      messages.error("Could not build the file");
    } finally {
      setPreparingCsv(false);
    }
  };

  const downloadSample = () =>
    downloadWithCityIds(
      csvStores.length ? templateRows(csvStores) : sampleRows([], regions, samplePlace(stores)),
      "Store Master - sample.csv"
    );

  const downloadAll = () => downloadWithCityIds(exportRows(csvStores), "Store Master.csv");

  // Every state and city with its id. Reads the cities of each state not
  // loaded yet, a few at a time.
  const downloadLocations = async () => {
    setDownloadingLocations(true);
    try {
      const stateList = states.length ? states : await loadStates();
      if (!stateList.length) return;
      const results = await mapWithConcurrency(stateList, 5, (state) => fetchCities(state._id));
      const failed = results.filter((result) => !result.success);
      if (failed.length) {
        messages.error(failed[0].message);
        return;
      }
      const fetched = Object.fromEntries(stateList.map((state, i) => [state._id, results[i].data]));
      setCitiesByState((current) => ({ ...current, ...fetched }));
      downloadCsv(locationRows(stateList, fetched), "States and Cities.csv");
    } catch (error) {
      console.error("States and cities download failed", error);
      messages.error("Could not build the file");
    } finally {
      setDownloadingLocations(false);
    }
  };

  const shown = pageOf(changedFirst(unsetFirst(rows, STORE_SETUP_FIELDS)), page, pageSize);
  // While the store list is loading the actions wait, since they would work on
  // a stale list. Hovering one says why (see DisabledHint).
  const loading = refreshing;
  const loadingHint = loading ? LOADING_TITLE : null;
  // Why Save can't be pressed right now, if it can't.
  const saveHint = loading
    ? LOADING_TITLE
    : saveCount === 0 && !saving
      ? "Nothing to save yet. Change a region, state or city, or upload a CSV, first."
      : null;

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div className="rp-page">
          <div className="rp-title">Store Master</div>
          <div className="rp-subtitle">
            {refreshing && stores.length === 0
              ? "Loading stores..."
              : `${plural(stores.length, "store")}${
                  rows.length !== stores.length ? ` · ${rows.length} shown` : ""
                } · set each store's region, state and city in the table`}
          </div>

          <div className="rp-filters">
            <div className="rp-field">
              <span className="rp-label">Region</span>
              <MultiSelect
                options={regionFilterOptions}
                value={regionFilter}
                onChange={setRegionFilter}
                summary={{ empty: "All regions", nouns: "regions" }}
                noOptionsMessage={() => "No regions"}
                width="220px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Search</span>
              <input
                className="dm-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Store, code, region, state or city"
              />
            </div>

            <DisabledHint message={loadingHint}>
              <button
                type="button"
                className="rp-action dm-secondary"
                onClick={() => {
                  loadRegions();
                  loadStates();
                  refresh();
                }}
                disabled={refreshing}
                title="Re-read stores, regions and states"
              >
                {refreshing ? "Loading..." : "Refresh"}
              </button>
            </DisabledHint>
          </div>

          {/* Closed by default, so the table starts higher up the page. */}
          <details className="dm-howto">
            <summary className="dm-howto-title">Update from a CSV file</summary>
            <ol className="dm-howto-steps">
              <li>
                <DisabledHint message={loadingHint}>
                  <button type="button" className="dm-link" onClick={downloadSample} disabled={loading}>
                    Download the sample CSV
                  </button>
                </DisabledHint>
                .{" "}
                {csvStores.length > 0
                  ? `It lists the ${plural(csvStores.length, "store")} shown, with their store codes and current values filled in.`
                  : "No stores are shown, so it has the headers and a few example rows - replace them with your store codes."}
              </li>
              <li>
                Change region, state or city where needed - when you change a state or city, put
                the new city's <b>cityId</b> in too. Leave the rest as they are.
              </li>
              <li>
                Upload it with <b>Upload CSV</b>, check the preview and Apply. The changes show in
                the table; press <b>Save changes</b> to send them.
              </li>
            </ol>
            <table className="dm-howto-columns">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Required</th>
                  <th>What to enter</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>storeCode</code>
                  </td>
                  <td>
                    <span className="dm-req">Yes</span>
                  </td>
                  <td>The store to update. Filled in for the stores shown.</td>
                </tr>
                <tr>
                  <td>
                    <code>region</code>
                  </td>
                  <td rowSpan={4}>
                    <span className="dm-req">At least one</span>
                  </td>
                  <td>One of the regions in the Region dropdown. Blank keeps the current region.</td>
                </tr>
                <tr>
                  <td>
                    <code>state</code>
                  </td>
                  <td>
                    The state's name. Close misspellings get a "did you mean" in the preview.
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>city</code>
                  </td>
                  <td>The city's name, for reading. Can be blank; if given it must match the cityId.</td>
                </tr>
                <tr>
                  <td>
                    <code>cityId</code>
                  </td>
                  <td>
                    <b>Needed to change the state or city</b>, since two cities can share a name. Get
                    it from <b>States &amp; Cities CSV</b>; it must be a city in the row's state. The
                    downloaded files fill it in for the current cities. State, city and cityId all
                    blank, or left as they are, keep the current location.
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>cityCode</code>
                  </td>
                  <td>The city's code, for reading. The upload ignores it.</td>
                </tr>
              </tbody>
            </table>
          </details>

          <div className="sa-toolbar">
            <div className="sa-summary">
              {loading
                ? "Loading stores... actions are paused until it finishes"
                : editCount > 0
                ? `${plural(editCount, "unsaved change")}${
                    requests.incomplete.length
                      ? ` · ${requests.incomplete.length} waiting for a city`
                      : ""
                  }`
                : stores.length > 0
                  ? "No unsaved changes"
                  : ""}
            </div>
            <div className="dm-actions">
              <DisabledHint message={loadingHint}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={downloadAll}
                  disabled={csvStores.length === 0 || preparingCsv || loading}
                  title="Every column, store name included, in the format Upload CSV reads back"
                >
                  Download CSV
                </button>
              </DisabledHint>
              <DisabledHint message={loadingHint}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={downloadSample}
                  disabled={preparingCsv || loading}
                  title={
                    csvStores.length
                      ? `storeCode, region, state, city and cityId for the ${plural(csvStores.length, "store")} shown`
                      : "The headers and a few example rows"
                  }
                >
                  Sample CSV
                </button>
              </DisabledHint>
              <DisabledHint message={loadingHint}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={downloadLocations}
                  disabled={downloadingLocations || loading}
                  title="Every state and city with its id"
                >
                  {downloadingLocations ? "Downloading..." : "States & Cities CSV"}
                </button>
              </DisabledHint>
              <DisabledHint message={loadingHint}>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={() => fileInput.current?.click()}
                  disabled={readingCsv || saving || loading}
                  title="Update regions and locations from a CSV"
                >
                  {readingCsv ? "Reading..." : "Upload CSV"}
                </button>
              </DisabledHint>
              <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
              {editCount > 0 && (
                <DisabledHint message={loadingHint}>
                  <button
                    type="button"
                    className="rp-action dm-secondary"
                    onClick={() => setConfirmingDiscard(true)}
                    disabled={saving || loading}
                  >
                    Discard
                  </button>
                </DisabledHint>
              )}
              <DisabledHint message={saveHint}>
                <button
                  type="button"
                  className="rp-action rp-action-primary"
                  onClick={save}
                  disabled={saveCount === 0 || saving || loading}
                  title={`Save region and location changes for ${plural(saveCount, "store")}`}
                >
                  {saving ? "Saving..." : `Save changes${saveCount ? ` (${saveCount})` : ""}`}
                </button>
              </DisabledHint>
            </div>
          </div>

          {csvPlan && (
            <div className="dm-csv">
              <div className="dm-csv-title">
                {csvName}:{" "}
                {csvPlan.total
                  ? `${plural(csvPlan.total, "store")} will change`
                  : "nothing to change"}
                {csvPlan.alreadySet > 0 && `, ${csvPlan.alreadySet} already in that city`}
                {csvPlan.errors.length > 0 && `, ${plural(csvPlan.errors.length, "line")} skipped`}
              </div>

              {(Object.keys(csvPlan.groups).length > 0 ||
                Object.keys(csvPlan.locationGroups).length > 0) && (
                <div className="dm-csv-changes">
                  {Object.entries(csvPlan.groups).map(([name, codes]) => (
                    <div key={`region-${name}`} className="dm-csv-line">
                      <Chip label={name} size="small" className="sm-chip sm-chip-region" />
                      {plural(codes.length, "store")}: {codes.join(", ")}
                    </div>
                  ))}
                  {Object.values(csvPlan.locationGroups).map((group) => (
                    <div key={`city-${group.cityId}`} className="dm-csv-line">
                      <Chip
                        label={`${group.city}, ${group.state}`}
                        size="small"
                        className="sm-chip sm-chip-location"
                      />
                      {plural(group.storeCodes.length, "store")}: {group.storeCodes.join(", ")}
                    </div>
                  ))}
                </div>
              )}

              {csvPlan.errors.length > 0 && (
                <div className="dm-csv-errors">
                  {csvPlan.errors.map((error, index) => (
                    <div key={`${error.line}-${index}`}>
                      {error.line ? `Line ${error.line}: ` : ""}
                      {error.message}
                    </div>
                  ))}
                </div>
              )}

              <div className="dm-actions" style={{ marginTop: "12px" }}>
                <DisabledHint message={loadingHint}>
                  <button
                    type="button"
                    className="rp-action rp-action-primary"
                    onClick={applyCsv}
                    disabled={csvPlan.total === 0 || loading}
                  >
                    Apply ({csvPlan.total})
                  </button>
                </DisabledHint>
                <button
                  type="button"
                  className="rp-action dm-secondary"
                  onClick={() => {
                    setCsvPlan(null);
                    setCsvName("");
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="dm-csv-hint">
                Blue chips set a region, green chips a location. Apply puts the changes in the
                table; Save changes sends them.
              </div>
            </div>
          )}

          <ActiveFilters
            specs={COLUMN_FILTERS}
            labels={FILTER_LABELS}
            filters={columnFilters}
            options={columnFilterOptions}
            onChange={setColumnFilter}
            onClear={() => setColumnFilters({})}
          />

          <div className="rp-table-wrap dm-table-page">
            <table className="rp-table dm-table">
              <thead>
                <tr>
                  {COLUMN_FILTERS.map(({ field, kind }, index) => (
                    <FilterHeaderCell
                      key={field}
                      label={COLUMNS[index]}
                      kind={kind}
                      value={columnFilters[field]}
                      options={columnFilterOptions[field]}
                      onChange={(value) => setColumnFilter(field, value)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.rows.length === 0 ? (
                  <tr>
                    <td className="rp-empty" colSpan={COLUMNS.length}>
                      {refreshing && stores.length === 0
                        ? "Loading stores..."
                        : stores.length === 0
                          ? "No stores found."
                          : "No stores match these filters."}
                    </td>
                  </tr>
                ) : (
                  shown.rows.map((row) => {
                    const state = rowState(row);
                    const stateId = state?._id;
                    const cities = stateId ? citiesByState[stateId] ?? [] : [];
                    return (
                      <tr
                        key={row.code}
                        className={row.changed ? "dm-row-changed" : unsetCount(row, STORE_SETUP_FIELDS) > 0 ? "dm-row-unset" : undefined}
                      >
                        <td className="dm-mono">
                          {row.code}
                          {row.changed ? (
                            <span className="dm-pending-tag">Unsaved</span>
                          ) : (
                            unsetCount(row, STORE_SETUP_FIELDS) > 0 && <span className="dm-unset-tag">Not set</span>
                          )}
                        </td>
                        <td className="dm-store">{row.name}</td>
                        <td>
                          <Select
                            options={regionSelectOptions}
                            value={asOption(row.region)}
                            onChange={(option) =>
                              option && update((current) => setRegionEdit(current, row, option.value))
                            }
                            placeholder="Select region..."
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={cellSelectStyles("190px")}
                            isDisabled={loading}
                            aria-label={`Region for ${row.code}`}
                          />
                        </td>
                        <td>
                          <Select
                            options={stateSelectOptions}
                            value={row.state ? { value: stateId ?? row.state, label: row.state } : null}
                            onChange={(option) =>
                              option && update((current) => setStateEdit(current, row, option.state))
                            }
                            placeholder="Select state..."
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={cellSelectStyles("190px")}
                            isDisabled={loading}
                            aria-label={`State for ${row.code}`}
                          />
                        </td>
                        <td>
                          <Select
                            options={cities.map((city) => ({ value: city._id, label: city.name, city }))}
                            value={row.city ? { value: row.cityId ?? row.city, label: row.city } : null}
                            onChange={(option) =>
                              option &&
                              update((current) => setCityEdit(current, row, state, option.city))
                            }
                            onMenuOpen={() => loadCities(stateId)}
                            isLoading={Boolean(stateId && loadingCities[stateId])}
                            isDisabled={!stateId || loading}
                            placeholder={
                              !stateId
                                ? "Pick a state first"
                                : row.locationIncomplete
                                  ? "Pick a city"
                                  : "Select city..."
                            }
                            noOptionsMessage={() =>
                              stateId && loadingCities[stateId] ? "Loading cities..." : "No cities"
                            }
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={
                              row.locationIncomplete
                                ? needsCityStyles("190px")
                                : cellSelectStyles("190px")
                            }
                            aria-label={`City for ${row.code}`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {rows.length > 0 && (
            <div className="sa-pager">
              <span>Rows per page</span>
              <Select
                options={PAGE_SIZE_OPTIONS}
                value={PAGE_SIZE_OPTIONS.find((option) => option.value === pageSize)}
                onChange={(option) => {
                  setPage(Math.floor(shown.start / option.value) + 1);
                  setPageSize(option.value);
                }}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectStyles("120px")}
              />
              <span>
                Rows {shown.start + 1}-{shown.start + shown.rows.length} of {rows.length}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page - 1)}
                disabled={shown.page === 1}
              >
                Previous
              </button>
              <span>
                Page {shown.page} of {shown.pages}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page + 1)}
                disabled={shown.page === shown.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmingDiscard}
        tone="danger"
        title="Discard unsaved changes?"
        message={`${plural(editCount, "store")} ${
          editCount === 1 ? "has" : "have"
        } changes to regions, states or cities that haven't been saved. Discarding puts ${
          editCount === 1 ? "it" : "them"
        } back to the saved values. This can't be undone.`}
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={discard}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  );
}

export default StoreRegions;
